import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getHealth } from '../api/health'
import { getProjects } from '../api/projects'
import { getStorageUsage } from '../api/storage'
import { getTokenUsage } from '../api/token-usage'
import { getUsageSummary } from '../api/usage'
import DashboardTokenSavingsCards from '../components/token-savings/DashboardTokenSavingsCards'
import { useAuth } from '../hooks/useAuth'
import type {
  HealthResponse,
  ProjectResponse,
  StorageUsageResponse,
  TokenUsageSummary,
  UsageLiveSnapshot,
} from '../types/api'
import { formatFileSize, formatUptime } from '../utils/format'
import { isGlobalAdmin } from '../utils/rbac'

export default function DashboardPage() {
  const { user } = useAuth()
  const isAdmin = isGlobalAdmin(user?.role)
  const [projects, setProjects] = useState<ProjectResponse[]>([])
  const [health, setHealth] = useState<HealthResponse | null>(null)
  const [usage, setUsage] = useState<UsageLiveSnapshot | null>(null)
  const [storage, setStorage] = useState<StorageUsageResponse | null>(null)
  const [storageFailed, setStorageFailed] = useState(false)
  const [storageOpen, setStorageOpen] = useState(false)
  const [tokenUsage, setTokenUsage] = useState<TokenUsageSummary | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsData, healthData, usageData, storageResult, tokenUsageResult] =
          await Promise.all([
            getProjects(),
            getHealth(),
            getUsageSummary(24).catch(() => null),
            getStorageUsage()
              .then((data) => ({ ok: true as const, data }))
              .catch(() => ({ ok: false as const, data: null })),
            getTokenUsage(30)
              .then((data) => ({ ok: true as const, data }))
              .catch(() => ({ ok: false as const, data: null })),
          ])
        setProjects(projectsData)
        setHealth(healthData)
        setUsage(usageData?.live ?? null)
        if (storageResult.ok) {
          setStorage(storageResult.data)
          setStorageFailed(false)
        } else {
          setStorage(null)
          setStorageFailed(true)
        }
        if (tokenUsageResult.ok) {
          setTokenUsage(tokenUsageResult.data)
        }
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    )
  }

  const failedTotal = usage ? usage.httpErrors + usage.mcpHttpErrors + usage.mcpToolErrors : null
  const storageTotalLabel = storageFailed ? '—' : storage ? formatFileSize(storage.totalBytes) : '—'
  const storageFilesBytes = (() => {
    if (storageFailed || !storage) return null
    if (storage.totalFilesBytes !== undefined) return storage.totalFilesBytes
    // Fallback for older API without aggregated fields
    try {
      let sum = 0n
      for (const p of storage.projects) {
        sum += BigInt(p.uploadBytes) + BigInt(p.objectStorageBytes) + BigInt(p.rawDataBytes)
      }
      if (storage.unattributedRawDataBytes) sum += BigInt(storage.unattributedRawDataBytes)
      return sum.toString()
    } catch {
      return '0'
    }
  })()
  const storageVectorsBytes = (() => {
    if (storageFailed || !storage) return null
    if (storage.totalVectorBytes !== undefined) return storage.totalVectorBytes
    try {
      let sum = 0n
      for (const p of storage.projects) sum += BigInt(p.vectorBytes)
      return sum.toString()
    } catch {
      return '0'
    }
  })()
  const storageFilesLabel = storageFilesBytes === null ? '—' : formatFileSize(storageFilesBytes)
  const storageVectorsLabel =
    storageVectorsBytes === null ? '—' : formatFileSize(storageVectorsBytes)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-harbor-text">Dashboard</h1>
        <p className="text-harbor-text-secondary mt-1">Overview of your RAG system</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Link
          to="/projects"
          className="bg-harbor-surface border border-harbor-border rounded-xl p-6 hover:border-emerald-500/50 transition-colors group"
        >
          <p className="text-sm font-medium text-harbor-text-secondary group-hover:text-emerald-400 transition-colors">
            Projects
          </p>
          <p className="mt-2 text-3xl font-bold text-harbor-text">{projects.length}</p>
        </Link>

        <div className="bg-harbor-surface border border-harbor-border rounded-xl p-6">
          <p className="text-sm font-medium text-harbor-text-secondary">
            {isAdmin ? 'Total storage' : 'Accessible storage'}
          </p>
          <p className="mt-2 text-3xl font-bold text-harbor-text">{storageTotalLabel}</p>
          <p className="text-xs text-harbor-text-muted mt-1">
            {isAdmin ? 'All projects on disk' : 'Sum of projects you can access'}
          </p>
          {!storageFailed && storage && storage.limitBytes && BigInt(storage.limitBytes) > 0n && (
            <div className="mt-3">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-harbor-text-secondary">
                  Limit: {formatFileSize(storage.limitBytes)}
                </span>
                {storage.overLimit && <span className="text-red-400 font-medium">Over limit</span>}
              </div>
              <div className="w-full bg-harbor-border rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${storage.overLimit ? 'bg-red-500' : 'bg-emerald-500'}`}
                  style={{
                    width: `${Math.min(100, Number((BigInt(storage.totalBytes) * 100n) / BigInt(storage.limitBytes)))}%`,
                  }}
                />
              </div>
              {storage.postgresBytes && storage.memoryBytes && (
                <p className="text-xs text-harbor-text-muted mt-1.5">
                  Includes ~{formatFileSize(storage.postgresBytes)} Postgres + ~
                  {formatFileSize(storage.memoryBytes)} memory (approximate)
                </p>
              )}
            </div>
          )}
          {!storageFailed && storage && (
            <div className="mt-3 pt-3 border-t border-harbor-border space-y-1.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-harbor-text-secondary">Files uploaded</span>
                <span className="font-medium text-harbor-text">{storageFilesLabel}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-harbor-text-secondary">LanceDB vectors</span>
                <span className="font-medium text-harbor-text">{storageVectorsLabel}</span>
              </div>
            </div>
          )}
        </div>

        <div className="bg-harbor-surface border border-harbor-border rounded-xl p-6">
          <p className="text-sm font-medium text-harbor-text-secondary">Server Status</p>
          <div className="mt-2 flex items-center gap-2">
            <span className="w-3 h-3 bg-emerald-500 rounded-full"></span>
            <span className="text-lg font-semibold text-emerald-400">Online</span>
          </div>
        </div>

        <div className="bg-harbor-surface border border-harbor-border rounded-xl p-6">
          <p className="text-sm font-medium text-harbor-text-secondary">Version</p>
          <p className="mt-2 text-3xl font-bold text-harbor-text">{health?.version || '—'}</p>
        </div>

        <div className="bg-harbor-surface border border-harbor-border rounded-xl p-6">
          <p className="text-sm font-medium text-harbor-text-secondary">Uptime</p>
          <p className="mt-2 text-3xl font-bold text-harbor-text">
            {health ? formatUptime(health.uptime) : '—'}
          </p>
        </div>

        <div className="bg-harbor-surface border border-harbor-border rounded-xl p-6">
          <p className="text-sm font-medium text-harbor-text-secondary">LLM tokens (30d)</p>
          <p className="mt-2 text-3xl font-bold text-harbor-text">
            {tokenUsage ? tokenUsage.totals.totalTokens.toLocaleString() : '—'}
          </p>
          {tokenUsage && (
            <p className="text-xs text-harbor-text-muted mt-1">
              {tokenUsage.totals.callCount.toLocaleString()} calls · {tokenUsage.perModel.length}{' '}
              model{tokenUsage.perModel.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      </div>

      {/* Storage breakdown */}
      {!storageFailed && storage && storage.projects.length > 0 && (
        <div className="bg-harbor-surface border border-harbor-border rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setStorageOpen((open) => !open)}
            aria-expanded={storageOpen}
            className="w-full flex items-center justify-between gap-2 p-6 text-left hover:bg-harbor-surface-raised/30 transition-colors"
          >
            <span>
              <span className="block text-lg font-semibold text-harbor-text">
                Storage by project
              </span>
              <span className="block text-sm text-harbor-text-secondary mt-1">
                {isAdmin
                  ? 'On-disk usage across all projects'
                  : 'On-disk usage for projects you can access'}
              </span>
            </span>
            <svg
              className={`w-5 h-5 shrink-0 text-harbor-text-muted transition-transform ${storageOpen ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
          {storageOpen && (
            <div className="divide-y divide-harbor-border border-t border-harbor-border">
              {storage.projects.map((row) => (
                <div
                  key={row.projectId}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-4"
                >
                  <div>
                    <p className="font-medium text-harbor-text">{row.projectName}</p>
                    <p className="text-xs text-harbor-text-muted mt-1">
                      Vectors {formatFileSize(row.vectorBytes)} · Uploads{' '}
                      {formatFileSize(row.uploadBytes)} · Objects{' '}
                      {formatFileSize(row.objectStorageBytes)} · Raw data{' '}
                      {formatFileSize(row.rawDataBytes)}
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-emerald-400 shrink-0">
                    {formatFileSize(row.totalBytes)}
                  </p>
                </div>
              ))}
              {isAdmin && storage.unattributedRawDataBytes !== undefined && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-4">
                  <div>
                    <p className="font-medium text-harbor-text">Unattributed raw data</p>
                    <p className="text-xs text-harbor-text-muted mt-1">
                      Legacy root raw-data not tied to a project
                    </p>
                  </div>
                  <p className="text-sm font-semibold text-emerald-400 shrink-0">
                    {formatFileSize(storage.unattributedRawDataBytes)}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Observability */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-harbor-surface border border-harbor-border rounded-xl p-6">
          <p className="text-sm font-medium text-harbor-text-secondary">In flight</p>
          <p className="mt-2 text-3xl font-bold text-harbor-text">{usage?.inFlight ?? '—'}</p>
          <p className="text-xs text-harbor-text-muted mt-1">
            Current concurrent requests / tool calls
          </p>
        </div>
        <div className="bg-harbor-surface border border-harbor-border rounded-xl p-6">
          <p className="text-sm font-medium text-harbor-text-secondary">Max concurrent</p>
          <p className="mt-2 text-3xl font-bold text-harbor-text">{usage?.inFlightMax ?? '—'}</p>
          <p className="text-xs text-harbor-text-muted mt-1">High-water mark since process start</p>
        </div>
        <div className="bg-harbor-surface border border-harbor-border rounded-xl p-6">
          <p className="text-sm font-medium text-harbor-text-secondary">Failed calls</p>
          <p className="mt-2 text-3xl font-bold text-harbor-text">{failedTotal ?? '—'}</p>
          <p className="text-xs text-harbor-text-muted mt-1">
            HTTP 4xx/5xx + MCP tool errors (live)
          </p>
        </div>
      </div>

      {/* Token savings (org admins: org-wide; managers: managed projects) */}
      <DashboardTokenSavingsCards isOrgAdmin={isAdmin} />

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link
          to="/projects"
          className="bg-harbor-surface border border-harbor-border rounded-xl p-6 hover:border-emerald-500/50 transition-colors group"
        >
          <div className="text-3xl mb-3">📁</div>
          <h3 className="font-semibold text-harbor-text group-hover:text-emerald-400 transition-colors">
            Manage Projects
          </h3>
          <p className="text-sm text-harbor-text-secondary mt-1">
            {isAdmin ? 'Create and manage your RAG projects' : 'View projects you can access'}
          </p>
        </Link>

        <Link
          to="/search"
          className="bg-harbor-surface border border-harbor-border rounded-xl p-6 hover:border-emerald-500/50 transition-colors group"
        >
          <div className="text-3xl mb-3">🔍</div>
          <h3 className="font-semibold text-harbor-text group-hover:text-emerald-400 transition-colors">
            Search Documents
          </h3>
          <p className="text-sm text-harbor-text-secondary mt-1">
            Search across your indexed documents
          </p>
        </Link>
      </div>
    </div>
  )
}
